import { useEffect, useMemo, useState } from 'react';
import type { ComponentSpacing, SignageComponent, SliderConfig } from '@/config/types';
import { useStableIds } from '@/hooks/useStableIds';
import { ProgressBar } from '@/widgets/ProgressBar/ProgressBar';
import { buildSlides } from '@/widgets/slides';
import { componentSpacingVars } from '@/widgets/spacing';
import { WidgetGrid } from '@/widgets/WidgetGrid';

/**
 * The slider rendering path: components grouped onto slides by `component.slide`, one slide
 * visible at a time, advancing every `slider.interval` seconds. Sibling of `FlatLayout`;
 * `ModuleLoader` picks between them on the presence of `config.slider`.
 *
 * DOM structure is the legacy `v-if="haveSlider"` branch of `moduleLoader.vue`, class for
 * class:
 *
 * ```
 * div.slider.px-0.w-full.min-h-screen
 *   div.slides-container                        (flex-grow, overflow hidden)
 *     div.{efect}.slide.{active|unactive} × n   (absolute, all present from the first paint)
 *       div[style="width:100%"]                 (see "the classless div" below)
 *         div.widget-grid.w-full.h-full         (the shared 12-column grid)
 *           div.widget-container × n            (same spacing as FlatLayout)
 *   <ProgressBar />                             (when slider.showprogressBar)
 * ```
 *
 * ## Every slide's widgets mount up front, and stay mounted
 *
 * This is the constraint the whole file is shaped around, because breaking it breaks a
 * display silently.
 *
 * SYM-489's connect gate snapshots `topicItems.keys()` **once**, when
 * `preventerLoaded && moduleLoaded && topicCount > 0` first becomes true. That one list is
 * sent to the preventer's `savemeta` endpoint — which is what provisions the broker ACL — and
 * is then the list MQTT subscribes with (`MqttProvider` effects 2 and 3). A topic registered
 * after that moment is in neither list. There is no error and no failed request: the widget
 * simply never receives a message and shows its empty state forever, on a wall, in another
 * country.
 *
 * The obvious implementation — render only the active slide — would register slide 2's topics
 * ten seconds after boot, long after the ACL was requested. So every slide is rendered on
 * mount and never unmounted; inactive ones are hidden with `opacity` and `transform`, which is
 * what the legacy app does anyway since both its effects are transitions between two
 * simultaneously-present absolutely-positioned elements.
 *
 * It is also the behaviour parity demands: legacy slides are all in the DOM from the first
 * paint, which is why a slide's data is already current when it becomes visible instead of
 * arriving a second later.
 *
 * ## State, not `classList`
 *
 * Legacy kept a `ref` array of slide elements, read `classList.contains('active')` off the DOM
 * to find the current one, and mutated classes on two of them per tick. Here there is one
 * `activeIndex` and the className is derived from it. That also fixes the leak: legacy's
 * `sliderInterval` is a module-level variable, so it is never cleared on unmount and a second
 * `ModuleLoader` would overwrite the first's handle (W-5).
 *
 * ## The classless div
 *
 * `<div :class="rootLayoutClass" style="width:100%">` in the legacy template binds a variable
 * that does not exist — its declaration is commented out two hundred lines below
 * (`//let rootLayoutClass = 'main-container  px-4';`). Vue renders no class attribute for it.
 * So a slide does **not** get `main-container`'s `padding-inline: 0.5rem`, and the div is kept
 * here exactly as bare as it renders today. The per-widget spacing from `layout.components` is
 * unaffected — it comes from the custom properties on the root, below.
 */

type SliderLayoutProps = {
  slider: SliderConfig;
  /** The whole config list; grouping onto slides happens here. */
  components: SignageComponent[];
  spacing: ComponentSpacing;
};

export function SliderLayout({ slider, components, spacing }: SliderLayoutProps) {
  const getId = useStableIds();
  const [activeIndex, setActiveIndex] = useState(0);

  const slideCount = slider.slides.length;

  // The config is immutable at runtime, so this runs exactly once — which is the point. The
  // ids below are keyed on `item.index`, and re-deriving the model on every render would be
  // harmless only for as long as the grouping stayed deterministic (CLAUDE.md constraint 3).
  const slides = useMemo(() => buildSlides(components, slideCount), [components, slideCount]);

  const intervalMs = slider.interval * 1000;

  /**
   * The single timing source. The slide advance and the progress sweep used to be two
   * independent intervals that started at slightly different moments and never resynced, so
   * after an hour the bar visibly completed before or after the slide changed. Here the bar is
   * keyed on `activeIndex`: this timer is the only thing that moves, and every tick remounts
   * the bar, which restarts its CSS animation from zero at the instant the slide changes. The
   * two cannot drift because there is nothing to drift against.
   *
   * Guarded on both inputs. Fewer than two slides means there is nothing to advance to, and a
   * non-positive `interval` would make `setInterval` fire at the 4ms clamp — a config typo
   * that would otherwise pin a display's CPU for weeks (CLAUDE.md constraint 5).
   */
  useEffect(() => {
    if (slideCount < 2 || intervalMs <= 0) return;

    const handle = setInterval(() => {
      setActiveIndex((current) => (current + 1) % slideCount);
    }, intervalMs);

    return () => {
      clearInterval(handle);
    };
  }, [slideCount, intervalMs]);

  return (
    // The spacing custom properties sit here rather than on each slide: one write of three
    // properties, inherited by every `.widget-container` below, exactly as `FlatLayout` puts
    // them on `.main-container`.
    <div className="slider px-0 w-full min-h-screen" style={componentSpacingVars(spacing)}>
      <div className="slides-container">
        {slides.map((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          return (
            <div
              // Slides never reorder — the array is `slider.slides` — so the position is a
              // stable key. Note this must *not* be `activeIndex`-dependent: a changing key
              // would remount the slide and with it every widget on it.
              key={slideIndex}
              data-slide-index={slideIndex}
              className={`${slider.efect} slide ${active ? 'active' : 'unactive'}`}
              // An inactive slide is invisible but still in the DOM, so it must not be
              // reachable by assistive tech or steal focus. There is no keyboard on a signage
              // display, but `inert` is one attribute and it is the correct one.
              aria-hidden={!active || undefined}
              inert={!active}
            >
              <div style={{ width: '100%' }}>
                {/*
                 * The same 12-column grid the flat path uses, so `span` means the same thing
                 * on a slide as it does off one. `index` is the counter that runs across
                 * slides, so it is what legacy passed to `generateID(component.index)`; keying
                 * the id on the position *within* the slide would collide the first component
                 * of every slide onto one id.
                 */}
                <WidgetGrid
                  items={slide.components.map(({ component, index }) => ({
                    component,
                    componentId: getId(index),
                    span: component.span,
                  }))}
                />
              </div>
            </div>
          );
        })}
      </div>
      {slider.showprogressBar && (
        <ProgressBar key={activeIndex} interval={slider.interval} layout={slider.layout} />
      )}
    </div>
  );
}
